import { styled, FormHelperText, Popover, Autocomplete, TextField, Fade, Box, Button, InputLabel } from '@mui/material';
import { LanguageRounded as LanguageRoundedIcon, ArrowDropDownRounded as ArrowDropDownRoundedIcon } from '@mui/icons-material';
import type { InputLabelProps } from '@mui/material';
import type { CountryCode, E164Number } from 'libphonenumber-js/core';
import type { PopperProps } from '@mui/material/Popper';
import { AsYouType, parseDigits, parsePhoneNumberFromString } from 'libphonenumber-js';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AnimationEvent, MouseEvent } from 'react';
import { getCountryCallingCode, isSupportedCountry } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import { useAppSelector } from '../_hooks/useAppSelector';
import useId from '@mui/utils/useId';
import { normalizePhoneNumberValue } from './PhoneUtils';

const StyledPhoneField = styled('div')(({theme}) => ({
  border: 0,
  outline: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  borderRadius: '30px',
  overflow: 'hidden',
  position: 'relative',
  transition: 'background-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
  padding: 0,
  display: 'flex',
  alignItems: 'stretch',

  '&:not(.Mui-disabled)': {
    '&:hover, &:focus-within, &.Mui-focused': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
    '&.Mui-autofilled, &:has(input:-webkit-autofill)': {
      backgroundColor: '#e8f0fe',
    },
    '&.Mui-autofilled:hover, &.Mui-autofilled:focus-within, &.Mui-autofilled.Mui-focused, &:has(input:-webkit-autofill):hover, &:has(input:-webkit-autofill):focus-within, &.Mui-focused:has(input:-webkit-autofill)': {
      backgroundColor: '#dce8fb',
    },
  },
}))

const StyledPhoneInput = styled(Input)(({theme}) => ({
  margin: 0,
  padding: 0,
  border: 0,
  outline: 0,
  background: 'none',
  color: 'currentColor',
  font: 'inherit',
  letterSpacing: 'inherit',
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
  minHeight: '43px',
  padding: '10px 16px 10px 66px',

  '&[disabled]': {
    color: 'rgba(0, 0, 0, 0.38)',
    cursor: 'default',
  },

  '&:-webkit-autofill': {
    animationName: 'phone-field-autofill-start',
    animationDuration: '1ms',
    WebkitBoxShadow: '0 0 0 100px #e8f0fe inset',
    WebkitTextFillColor: 'inherit',
    caretColor: 'inherit',
  },

  '@keyframes phone-field-autofill-start': {
    from: {},
    to: {},
  },
}))

const StyledIconButton = styled(Button)(({theme}) => ({
  alignItems: 'center',
  color: 'rgba(0, 0, 0, 0.54)',
  display: 'inline-flex',
  inset: '0 auto 0 0',
  margin: 0,
  minWidth: 0,
  padding: '0 8px 0 16px',
  position: 'absolute',
  zIndex: 1,
}))

interface CountryOption {
  id: number
  label: string
}

const InlineAutocompletePopper = forwardRef<HTMLDivElement, PopperProps>(function InlineAutocompletePopper(props, ref) {
  const renderedChildren = typeof props.children === 'function'
    ? props.children({ placement: props.placement ?? 'bottom' })
    : props.children

  if (!props.open && !props.keepMounted) return null

  return <>{renderedChildren}</>
})

export interface PhoneFieldProps {
  id?: string
  label?: React.ReactNode
  defaultCountry: number
  value?: E164Number
  onChange: (value?: E164Number) => void
  disabled?: boolean,
  error?: boolean
  helperText?: React.ReactNode
  InputLabelProps?: Omit<InputLabelProps, "children" | "ref">
}

export default function PhoneField(props: PhoneFieldProps) {
  const id = useId(props.id)

  const countryInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  //Filter countries not supported by the Phone input library
  const countries = useAppSelector(state => state.get('appData').get('countries'))

  const [listOpened,setListOpened] = useState(false)
  const [listAnchorEl,setListAnchorEl] = useState<HTMLButtonElement|null>(null)
  const [autofilled, setAutofilled] = useState(false)
  const [focused, setFocused] = useState(false)
  const [phoneInputResetKey, setPhoneInputResetKey] = useState(0)

  const defaultCountryId = useMemo(() => {
    if (props.defaultCountry) return props.defaultCountry

    if (!window.APP_GEO_COUNTRYCODE) return 0

    return countries.find(country => country.get('alpha2_code') === window.APP_GEO_COUNTRYCODE)?.get('id') ?? 0
  }, [countries, props.defaultCountry])

  const previousDefaultCountryIdRef = useRef<number>(defaultCountryId)
  const [countryId,setCountryId] = useState<number|null>(defaultCountryId);

  const countryOptionValue = useMemo<CountryOption | null>(() => {
    const countryObj = countries.get(String(countryId))
    if (!countryObj) return null;

    const countryCode = countryObj.get('alpha2_code') as CountryCode;

    return {
      id: countryObj.get('id'),
      label: countryObj.get('name')+ (isSupportedCountry(countryCode) ? ' (+'+getCountryCallingCode(countryCode)+')' : ''),
    }
  }, [countryId, countries])

  const countryCode = useMemo(() => {
    const countryObj = countries.get(String(countryId))
    if (!countryObj) return undefined

    return countryObj.get('alpha2_code') as CountryCode
  }, [countries, countryId])

  const phoneDefaultCountryCode = useMemo(() => {
    return countryCode && isSupportedCountry(countryCode) ? countryCode : undefined
  }, [countryCode])

  const phoneValue = useMemo(() => {
    return normalizePhoneNumberValue(props.value, phoneDefaultCountryCode)
  }, [phoneDefaultCountryCode, props.value])

  const getSupportedCountryCodeForCountryId = useCallback((countryId: number | null) => {
    const countryObj = countries.get(String(countryId))
    if (!countryObj) return undefined

    const countryCode = countryObj.get('alpha2_code') as CountryCode
    return isSupportedCountry(countryCode) ? countryCode : undefined
  }, [countries])

  const getPhoneValueForCountryChange = useCallback((nextCountryCode?: CountryCode) => {
    if (!nextCountryCode) return props.value

    const phoneInputValue = phoneInputRef.current?.value.trim() ?? ''
    if (!phoneInputValue) return undefined

    const phoneDigits = parseDigits(phoneInputValue)
    if (!phoneDigits) return undefined

    const nextCallingCode = getCountryCallingCode(nextCountryCode)
    const previousCallingCode = phoneDefaultCountryCode && phoneDefaultCountryCode !== nextCountryCode
      ? getCountryCallingCode(phoneDefaultCountryCode)
      : undefined

    let nationalDigits = phoneDigits

    if (phoneInputValue.startsWith('+')) {
      if (phoneDigits.startsWith(nextCallingCode)) {
        return `+${phoneDigits}`
      }

      if (previousCallingCode && phoneDigits.startsWith(previousCallingCode)) {
        nationalDigits = phoneDigits.substring(previousCallingCode.length)
      } else if (phoneDefaultCountryCode) {
        nationalDigits = parsePhoneNumberFromString(`+${phoneDigits}`)?.nationalNumber ?? phoneDigits
      }
    }

    const nextPhoneNumber = new AsYouType(nextCountryCode)
    nextPhoneNumber.input(nationalDigits)

    return nextPhoneNumber.getNumberValue()
  }, [phoneDefaultCountryCode, props.value])

  useEffect(() => {
    if (previousDefaultCountryIdRef.current === defaultCountryId) return

    previousDefaultCountryIdRef.current = defaultCountryId

    if (defaultCountryId) {
      const nextPhoneValue = getPhoneValueForCountryChange(getSupportedCountryCodeForCountryId(defaultCountryId))
      setCountryId(defaultCountryId)

      if (nextPhoneValue !== props.value) {
        props.onChange(nextPhoneValue)
      }
    }
  }, [defaultCountryId, getPhoneValueForCountryChange, getSupportedCountryCodeForCountryId, props.onChange, props.value])

  const countryOptions = useMemo<CountryOption[]>(() => {
    return countries.valueSeq().map(country => {

      const countryCode = country.get('alpha2_code') as CountryCode;

      return {
        id: country.get('id'),
        label: country.get('name')+ (isSupportedCountry(countryCode) ? ' (+'+getCountryCallingCode(countryCode)+')' : ''),
      }
    }).toArray()
  }, [countries])

  const onListOpen = useCallback((e:MouseEvent<HTMLButtonElement>) => {
    setListOpened(true)
    setListAnchorEl(e.currentTarget)
    
    setTimeout(() => {
      countryInputRef.current?.focus()
    })
  }, [])

  const onListClose = useCallback(() => {
    setListOpened(false)
  }, [])

  const onChange = useCallback((value?: E164Number) => {
    //Reset to default country if phone number is empty
    if(!value && defaultCountryId) {
      setCountryId(defaultCountryId)
    }

    props.onChange(value)
  }, [props.onChange, defaultCountryId])

  const onBlurPhone = useCallback(() => {
    if (phoneInputRef.current?.value.trim() === '+') {
      setPhoneInputResetKey(key => key + 1)
      props.onChange(undefined)
    }
  }, [props.onChange])

  const { InputLabelProps } = props;

  return <div>
    {props.label !== undefined && props.label !== null ? <InputLabel
      htmlFor={id}
      error={props.error}
      disabled={props.disabled}
      focused={focused || listOpened}
      {...InputLabelProps}
    >{props.label}</InputLabel> : <></>}

    <StyledPhoneField
      className={[
        props.disabled ? 'Mui-disabled' : '',
        autofilled ? 'Mui-autofilled' : '',
        listOpened ? 'Mui-focused' : '',
      ].filter(Boolean).join(' ') || undefined}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false)
        }
      }}
      onFocus={() => {
        setFocused(true)
      }}
    >
      <StyledIconButton
        variant="text"
        size="small"
        color="secondary"
        onClick={onListOpen}
        disabled={props.disabled}
      >
        { countryCode 
          ? <div style={{
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
                src={require('flag-icons/flags/4x3/'+countryCode.toLowerCase()+'.svg?url')}
                width={20}
                height={15}
                style={{
                  display: 'block',
                  transition: 'opacity 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
                  opacity: props.disabled ? 0.38 : 1,
                }}
              />
            </div>
          : <LanguageRoundedIcon fontSize="small"/>}
        <ArrowDropDownRoundedIcon/>
      </StyledIconButton>
      <StyledPhoneInput
        key={phoneInputResetKey}
        id={id}
        ref={phoneInputRef}
        //TODO: We have this as a hack for materializecss
        className="PhoneInputInput"
        defaultCountry={phoneDefaultCountryCode}
        smartCaret={phoneDefaultCountryCode !== undefined}
        value={phoneValue}
        onChange={onChange}
        onAnimationStart={(event: AnimationEvent<HTMLInputElement>) => {
          if (event.animationName === 'phone-field-autofill-start') {
            setAutofilled(true)
          }
        }}
        onBlur={onBlurPhone}
        autoComplete="tel"
        disabled={props.disabled}
      />
    </StyledPhoneField>

    {props.helperText !== undefined && props.helperText !== null && props.helperText !== '' ? <FormHelperText
      error={props.error}
    >{props.helperText}</FormHelperText> : null}

    <Popover
      open={listOpened}
      anchorEl={listAnchorEl}
      onClose={onListClose}
      //We use Fade to avoid issues with autocomplete positioning
      TransitionComponent={Fade}
      keepMounted
      slotProps={{
        paper: {
          style: {
            minWidth: '240px',
          }
        },
      }}
    >
      <Autocomplete
        options={countryOptions}
        value={countryOptionValue}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(e, value) => {
          const nextCountryId = value ? value.id : null
          const nextPhoneValue = getPhoneValueForCountryChange(getSupportedCountryCodeForCountryId(nextCountryId))

          if (nextPhoneValue !== props.value) {
            props.onChange(nextPhoneValue)
          }

          setCountryId(nextCountryId)
          setListOpened(false)
          setTimeout(() => {
            phoneInputRef.current?.focus()
          })
        }}
        autoComplete
        autoHighlight
        disableClearable={countryOptionValue !== null}
        openOnFocus
        disablePortal
        PopperComponent={InlineAutocompletePopper}
        PaperComponent={Box}
        renderInput={(params) => {
          return <TextField
            //label={t('Country')}
            inputRef={countryInputRef}
            {...params}
          />
        }}
      />
    </Popover>
  </div>
}
